import { FC } from 'react';

import { Box } from '@mui/material';
import { DateRangePickerDay, DateRangePickerDayProps } from '@mui/x-date-pickers-pro';

import { Dayjs } from 'dayjs';

import { DAY_CELL_GAP_PX } from './dayCalendarGridSx';

type Props = DateRangePickerDayProps<Dayjs> & {
  isDateUnavailable?: (date: Dayjs) => boolean;
  hasCompleteRange?: boolean;
};

const DAY_CELL_OVERSHOOT_PX = DAY_CELL_GAP_PX / 2;

const PILL_SX = {
  borderRadius: '12px !important',
};

const CalendarDateRangePickerDay: FC<Props> = ({
  isDateUnavailable,
  hasCompleteRange,
  ...dayProps
}) => {
  const {
    day,
    outsideCurrentMonth,
    today,
    selected,
    isHighlighting,
    isStartOfHighlighting,
    isEndOfHighlighting,
    isPreviewing,
    isStartOfPreviewing,
    isEndOfPreviewing,
    isFirstVisibleCell,
    isLastVisibleCell,
  } = dayProps;
  const unavailable = !outsideCurrentMonth && !!isDateUnavailable?.(day);
  const showTodayDot =
    today && !outsideCurrentMonth && !selected && !isHighlighting && !isPreviewing;

  const isPrevDayUnavailable = !!isDateUnavailable?.(day.subtract(1, 'day'));
  const isNextDayUnavailable = !!isDateUnavailable?.(day.add(1, 'day'));

  const isBarStart =
    isStartOfHighlighting || day.day() === 1 || isFirstVisibleCell || isPrevDayUnavailable;
  const isBarEnd =
    isEndOfHighlighting || day.day() === 0 || isLastVisibleCell || isNextDayUnavailable;
  const barBorderRadius =
    isBarStart && isBarEnd
      ? '12px'
      : isBarStart
        ? '12px 0 0 12px'
        : isBarEnd
          ? '0 12px 12px 0'
          : '0';

  const isRangeEndpoint = isStartOfHighlighting || isEndOfHighlighting;
  const needsRightBridge = isStartOfHighlighting && !isBarEnd;
  const needsLeftBridge = isEndOfHighlighting && !isBarStart;
  const leftOvershoot = isPrevDayUnavailable ? 0 : DAY_CELL_OVERSHOOT_PX;
  const rightOvershoot = isNextDayUnavailable ? 0 : DAY_CELL_OVERSHOOT_PX;
  const beforeLeft = isRangeEndpoint
    ? needsLeftBridge
      ? `-${leftOvershoot}px`
      : 0
    : `-${leftOvershoot}px`;
  const beforeWidth = isRangeEndpoint
    ? needsRightBridge
      ? `${40 + rightOvershoot}px`
      : needsLeftBridge
        ? `${40 + leftOvershoot}px`
        : '40px'
    : `${40 + leftOvershoot + rightOvershoot}px`;

  const showPreview =
    isPreviewing && !isHighlighting && !outsideCurrentMonth && !unavailable && !hasCompleteRange;
  const isPreviewBarStart = isStartOfPreviewing || day.day() === 1 || isFirstVisibleCell;
  const isPreviewBarEnd = isEndOfPreviewing || day.day() === 0 || isLastVisibleCell;
  const previewBarBorderRadius =
    isPreviewBarStart && isPreviewBarEnd
      ? '12px'
      : isPreviewBarStart
        ? '12px 0 0 12px'
        : isPreviewBarEnd
          ? '0 12px 12px 0'
          : '0';
  const previewBeforeLeft = isPreviewBarStart ? 0 : `-${leftOvershoot}px`;
  const previewBeforeWidth =
    isPreviewBarStart && isPreviewBarEnd
      ? '40px'
      : isPreviewBarStart
        ? `${40 + rightOvershoot}px`
        : isPreviewBarEnd
          ? `${40 + leftOvershoot}px`
          : `${40 + leftOvershoot + rightOvershoot}px`;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <DateRangePickerDay
        {...dayProps}
        disableHighlightToday
        sx={{
          position: 'relative',
          width: '40px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(isHighlighting &&
            !outsideCurrentMonth && {
              backgroundColor: 'transparent !important',
              borderRadius: '0 !important',
              ...(!unavailable && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: beforeLeft,
                  top: 0,
                  width: beforeWidth,
                  height: '36px',
                  borderRadius: barBorderRadius,
                  backgroundColor: (theme) => theme.palette.accent.light,
                },
              }),
            }),
          ...(showPreview && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: previewBeforeLeft,
              top: 0,
              width: previewBeforeWidth,
              height: '36px',
              borderRadius: previewBarBorderRadius,
              backgroundColor: (theme) => theme.palette.grey[100],
            },
          }),
          '& .MuiDateRangePickerDay-rangeIntervalPreview': {
            border: 'none !important',
            borderColor: 'transparent !important',
          },
          '& .MuiDateRangePickerDay-day': {
            transform: 'none !important',
          },
          '& .MuiPickersDay-root': {
            width: '40px',
            height: '36px',
            borderRadius: '12px',
            fontSize: '12px',
            lineHeight: 1.25,
            border: 'none',
            // MUI's default PickersDay applies a blue `primary.main`-based tint
            // on native DOM :focus, which the calendar's own roving-tabindex
            // can land on any cell (e.g. after clicking a preset button) —
            // regardless of selected/unavailable/highlighting state, so this
            // must always be cleared, not just for the plain unselected case.
            '&:focus': { backgroundColor: 'transparent !important' },
            ...(!selected &&
              !unavailable &&
              !isHighlighting && {
                '&:hover': {
                  backgroundColor: (theme) => `${theme.palette.grey[50]} !important`,
                  border: 'none !important',
                },
              }),
            '&.Mui-selected': {
              ...PILL_SX,
              width: '44px',
              height: '40px',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: 1,
              // The theme's global MuiPickersDay override sets its own
              // `&.Mui-selected` color at equal CSS specificity, so insertion
              // order (not intent) decides which one wins depending on which
              // MUI X component hosts this day slot. Force both color and
              // background so the pill renders consistently everywhere.
              color: '#FEFEFE !important',
              backgroundColor: (theme) => `${theme.palette.accent.main} !important`,
            },
            ...(unavailable && {
              ...PILL_SX,
              backgroundColor: (theme) => `${theme.palette.red[200]} !important`,
              color: (theme) => `${theme.palette.red[400]} !important`,
              fontWeight: 400,
              lineHeight: 1,
              textDecoration: 'line-through',
            }),
          },
        }}
      />
      {showTodayDot && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '5px',
            left: '18px',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            backgroundColor: (theme) => theme.palette.accent.main,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default CalendarDateRangePickerDay;
